import { createServerFn } from "@tanstack/react-start";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Self-serve privacy rights, exercised by the account holder themselves:
 *  - exportMyData  → access + portability (machine-readable copy)
 *  - deleteMyAccount → erasure
 *
 * Both read the caller's identity from the auth middleware, never from input,
 * so one member can never export or delete another member's data.
 */

/** Tables exported as-is, keyed by the column that identifies the member. */
const OWNED_TABLES: { table: string; column: string }[] = [
  { table: "posts", column: "author_id" },
  { table: "comments", column: "author_id" },
  { table: "votes", column: "user_id" },
  { table: "reactions", column: "user_id" },
  { table: "saves", column: "user_id" },
  { table: "post_followers", column: "user_id" },
  { table: "follows", column: "follower_id" },
  { table: "community_members", column: "user_id" },
  { table: "community_announcements", column: "author_id" },
  { table: "project_members", column: "user_id" },
  { table: "project_updates", column: "author_id" },
  { table: "official_responses", column: "author_id" },
  { table: "user_expertise", column: "user_id" },
  { table: "user_achievements", column: "user_id" },
  { table: "civic_progress", column: "user_id" },
  { table: "digest_subscriptions", column: "user_id" },
  { table: "notifications", column: "user_id" },
  { table: "notification_preferences", column: "user_id" },
  { table: "profile_media", column: "user_id" },
  { table: "conversation_members", column: "user_id" },
];

/**
 * Excluded from the export on purpose, and documented in the Privacy Policy:
 * activity_log, client_errors, email_events and user_roles are security,
 * reliability and moderation records rather than member content.
 */


export const exportMyData = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { supabase, userId } = context;

    const { data: profile } = await supabase.from("profiles").select("*").eq("id", userId).maybeSingle();

    // Serialized straight to a JSON download, so row shapes stay untyped here.
    const content: Record<string, any[]> = {};
    const unavailable: string[] = [];

    for (const { table, column } of OWNED_TABLES) {
      // Tables evolve; a missing one must not fail the whole export.
      const { data, error } = await supabase.from(table as never).select("*").eq(column, userId);
      if (error) {
        unavailable.push(table);
        continue;
      }
      content[table] = data ?? [];
    }

    return {
      export_generated_at: new Date().toISOString(),
      export_format_version: 1,
      about_this_file:
        "This is your Xamseen data export: the account profile you created and the content and preferences stored against your account. It does not include other people's content, or internal security and moderation logs which are retained for abuse prevention as described in the Xamseen Privacy Policy.",
      account: { id: userId },
      profile: profile ?? null,
      content,
      notes: unavailable.length ? { unavailable_sections: unavailable } : undefined,
    };
  });

export const deleteMyAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;

    // Admin client is required to remove the auth identity itself. Loaded only
    // after the middleware has established who the caller is.
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    // Remove owned rows first so nothing is orphaned where a cascade is absent.
    for (const { table, column } of OWNED_TABLES) {
      await supabaseAdmin.from(table as never).delete().eq(column, userId);
    }
    // Follows where this member is the target, not the follower.
    await supabaseAdmin.from("follows").delete().eq("following_id", userId);
    await supabaseAdmin.from("profiles").delete().eq("id", userId);

    const { error } = await supabaseAdmin.auth.admin.deleteUser(userId);
    if (error) throw new Error(`Account deletion failed: ${error.message}`);

    return { deleted: true };
  });
